import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useCurrency } from '@/contexts/CurrencyContext';
import { Search, X, Clock } from 'lucide-react';

const RECENT_KEY = 'cloudstore_recent_searches';

export default function SearchAutocomplete() {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    try { setRecent(JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').slice(0, 5)); } catch {}
  }, []);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/search/products?q=${encodeURIComponent(q)}&limit=5`);
      setResults(data);
    } catch { setResults([]); }
    setLoading(false);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelect = (handle, title) => {
    setOpen(false);
    setQuery('');
    const newRecent = [title, ...recent.filter(r => r !== title)].slice(0, 5);
    setRecent(newRecent);
    localStorage.setItem(RECENT_KEY, JSON.stringify(newRecent));
    navigate(`/product/${handle}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setOpen(false);
    const newRecent = [query.trim(), ...recent.filter(r => r !== query.trim())].slice(0, 5);
    setRecent(newRecent);
    localStorage.setItem(RECENT_KEY, JSON.stringify(newRecent));
    navigate(`/shop?search=${encodeURIComponent(query.trim())}`);
    setQuery('');
  };

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  if (!open) {
    return (
      <button data-testid="search-trigger" onClick={() => setOpen(true)}
        className="p-1.5 rounded-full transition-colors hover:bg-[rgba(26,26,26,0.05)]">
        <Search size={18} strokeWidth={1.5} style={{ color: '#1A1A1A' }} />
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50" onClick={() => setOpen(false)}>
      <div className="absolute inset-0 bg-black/10 backdrop-blur-sm" />
      <div className="relative max-w-xl mx-auto mt-20 px-4" onClick={e => e.stopPropagation()}>
        <form onSubmit={handleSubmit} className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(26,26,26,0.35)' }} />
          <input ref={inputRef} data-testid="search-input" type="text" value={query} onChange={handleChange}
            placeholder="Search products..."
            className="w-full pl-12 pr-12 py-4 font-sans text-base rounded-lg border-0 shadow-xl outline-none"
            style={{ backgroundColor: '#fff', color: '#1A1A1A' }} />
          <button type="button" onClick={() => setOpen(false)} className="absolute right-4 top-1/2 -translate-y-1/2 p-1">
            <X size={18} style={{ color: 'rgba(26,26,26,0.4)' }} />
          </button>
        </form>

        {(results.length > 0 || (query.length < 2 && recent.length > 0)) && (
          <div className="mt-2 rounded-lg shadow-xl overflow-hidden" style={{ backgroundColor: '#fff' }}>
            {query.length < 2 && recent.length > 0 && (
              <div className="px-4 py-2">
                <p className="font-sans text-[10px] uppercase tracking-wider mb-1" style={{ color: 'rgba(26,26,26,0.4)' }}>Recent</p>
                {recent.map(r => (
                  <button key={r} onClick={() => { setQuery(r); search(r); }}
                    className="flex items-center gap-2 w-full py-1.5 font-sans text-sm hover:text-[#E85D3A] transition-colors"
                    style={{ color: 'rgba(26,26,26,0.6)' }}>
                    <Clock size={13} /> {r}
                  </button>
                ))}
              </div>
            )}
            {results.map(r => (
              <button key={r.handle} data-testid={`search-result-${r.handle}`}
                onClick={() => handleSelect(r.handle, r.title)}
                className="flex items-center gap-3 w-full px-4 py-3 hover:bg-[rgba(26,26,26,0.02)] transition-colors border-t"
                style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
                <div className="w-10 h-12 flex-shrink-0 overflow-hidden rounded" style={{ backgroundColor: 'rgba(26,26,26,0.04)' }}>
                  {r.image && <img src={r.image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 text-left">
                  <p className="font-sans text-sm" style={{ color: '#1A1A1A' }}>{r.title}</p>
                  <p className="font-sans text-xs" style={{ color: '#E85D3A' }}>{formatPrice(r.price)}</p>
                </div>
              </button>
            ))}
            {query.length >= 2 && results.length === 0 && !loading && (
              <p className="px-4 py-4 font-sans text-sm text-center" style={{ color: 'rgba(26,26,26,0.4)' }}>No products found</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
